<template>
  <div class="data-holder performance">
    <div class="data-holder-inner">
      <div class="filter-holder row">
        <transition name="fade">
          <div class="filter" v-if="initialized">
            <el-date-picker
              v-model="dates"
              type="daterange"
              align="left"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              range-separator="-"
              start-placeholder="Start date"
              end-placeholder="End date"
              :picker-options="pickerOptions"
              style="width: 230px"
            >
            </el-date-picker>
            <transition name="fade">
              <i class="el-icon-refresh refresh" :class="{spin: refreshing}" v-if="initialized && dates && dates[0]" @click="!refreshing && requestStats(true)"></i>
            </transition>
            <el-popover v-if="initialized" ref="campaignSummary" v-model="campaignSummaryVisible"
                placement="bottom" title="Campaign Summary" trigger="click" popper-class="campaign-summary">
              <campaign-summary :campaign="this.item.id" v-clickoutside="() => campaignSummaryVisible && window.setTimeout(() => campaignSummaryVisible = false, 0)"></campaign-summary>
            </el-popover>
            <i class="el-icon-share share" v-popover:campaignSummary v-if="initialized"></i>
          </div>
        </transition>
      </div>
    </div>
    <div class="slidein touch-scroll">
      <div class="data-content content" :key="item && item.id">
        <div class="campaign-headers">
          <h1 v-if="item">{{ item.step1_campaign_name || item.step2_campaign_name }}</h1>
          <h2 v-if="item && item.step1_campaign_name && item.step2_campaign_name">{{ item.step2_campaign_name }}</h2>
        </div>
        <div v-if="initialized" class="affiliate-id">
          <transition name="fade" mode="out-in">
            <h1 v-if="!affiliateId" key="all">{{ affiliateId || 'All Affiliates' }}</h1>
          </transition>
        </div>
        <transition name="fade" mode="out-in">
          <div class="message" :class="{loading: loading}" v-if="loading || !initialized || message" key="message">
            <p v-if="message">{{ message }}</p>
            <div v-else-if="loading || !initialized" v-loading="true"></div>
          </div>

          <div class="performance-charts" v-else key="content">
            <el-alert v-if="!validCredentials" title="This integration currently has known issues, and the stats may be incorrect or delayed." type="error" class="error-bar"></el-alert>
            <el-alert v-if="lastUpdated" :title="lastUpdated" type="warning" class="error-bar"></el-alert>

            <el-collapse ref="rotator-collapse" v-if="hasStats && (step1Stats.length > 1 || step2Stats.length > 1)" v-model="activeTabs">
              <el-collapse-item title="Performance Graph" name="1">
                <el-select @change="newVar => { currentDataView = newVar }" :value="activeCampaignProps[currentDataView].label" placeholder="Select Data" class="chart-title">
                  <el-option v-for="(config, prop) in activeCampaignProps" v-if="!config.graphHidden" :key="prop" :value="prop" :label="config.label"></el-option>
                </el-select>
                <div class="chart-container" style="height: 400px; width: 100%">
                  <line-chart :upper-bound="allStats.variance.max" :lower-bound="allStats.variance.min" :chart-data="graphData"></line-chart>
                </div>
              </el-collapse-item>
            </el-collapse>

            <el-popover v-for="(config, prop) in activeCampaignProps" class="stat-popover" :ref="'stat-popover-' + prop" placement="top-start" :open-delay="150" :key="prop">
              <div class="stat-definition">
                <h1 class="stat-popover__title">{{ config.label }}</h1>
                <span class="stat-definition--content">{{ config.def }}</span>
              </div>
              <div class="stat-legend" v-if="attributeLegend[prop]">
                <h1 class="stat-popover__title stat-legend__title">Legend</h1>
                <ul>
                  <li v-for="(value, index) in attributeLegend[prop]" class="stat-legend__li" :key="index">
                    <template v-if="index < attributeLegend[prop].length - 2">
                      <div class="stat-legend__square" :class="colorCell(prop, attributeLegend[prop][index + 1].value)"></div>
                      {{ Math.round(value.value) }} - {{ Math.round(attributeLegend[prop][index + 1].value) }} %
                    </template>
                    <template v-else-if="index == attributeLegend[prop].length - 2">
                      <div class="stat-legend__square" :class="colorCell(prop, attributeLegend[prop][index + 1].value)"></div>
                      > {{ Math.round(value.value) }} %
                    </template>
                  </li>
                </ul>
              </div>
            </el-popover>

            <div v-for="(stats,stepIndex) in [step1Stats, step2Stats]" v-if="stats.length > 0 && dates[0]" :key="stepIndex" class="campaign-table-wrapper" @scroll.stop="stickyCell" @touchmove.stop>
              <div class="campaign-table-scroller" @scroll="stickyCell">
                <table class="campaign-table">
                  <tbody>
                    <tr v-for="(row, key) in stats" :key="key">
                      <td v-for="(stat, prop) in activeCampaignProps" v-if="stat.only == undefined || stat.only == stepIndex" :class="colorCell(prop, row[prop])" :key="prop"><span> {{ row[prop] }}</span></td>
                    </tr>
                  </tbody>
                  <thead>
                    <tr>
                      <th v-for="(stat, prop) in activeCampaignProps" :ref="'stat-header-' + prop" v-if="stat.only == undefined || stat.only == stepIndex"
                          :key="prop" @mouseenter="(ev) => showStatLegend(ev, stepIndex, prop)" @mouseleave="(ev) => hideStatLegend(ev, stepIndex, prop)">
                        {{ prop == 'date' ? 'Step ' + (stepIndex + 1) : stat.label }}
                      </th>
                    </tr>
                  </thead>
                </table>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { stringify } from 'qs'
import pickerShortcuts from './picker-shortcuts'
import CampaignSummary from './campaign-summary'
import { campaignStatProps } from './campaign-stat-props'
import $ from 'jquery'
import timeago from 'timeago'
import LineChart from './line-chart'
import datejs from '../static/js/date.js'
import Clickoutside from 'element-ui/src/utils/clickoutside';

export default {
  name: 'campaign',
  props: ['item'],
  components: { LineChart, CampaignSummary },
  directives: { Clickoutside },
  data() {
    return {
      window                   : window,
      loading                  : true,
      refreshing               : false,
      mouseEvent               : null,
      pickerOptions            : {
        shortcuts              : pickerShortcuts.pickerShortcuts,
        firstDayOfWeek         : 1,
        disabledDate           : (time) => {
          return time >= new Date()
        },
        // Note: date objects are passed by reference - modifying the object modifies the selection
        onPick                 : (selection) => {
          // Don't allow date windows >= 45 days
          if (Date.parse(selection.maxDate) > Date.parse(selection.minDate).addDays(45)) {
            this.$notify.error({
              title   : 'Error',
              message : 'Date ranges are limited to 45 day windows.',
              position: 'bottom-right'
            });

            selection.maxDate.addDays(45 - Math.floor((selection.maxDate.getTime() - selection.minDate) / (60*60*24*1000)))
          }
        }
      },
      dates                    : [
        Date.today().add(-6).days(),
        Date.today()
      ],
      campaignSummaryVisible   : false,
      requestedOverage         : false,
      previousFromDate         : null,
      previousToDate           : null,
      previousItem             : null,
      allStats                 : {},
      currentDataView          : 'sales',
      activeTabs               : this.$store.state.user.preferences.performanceTabs.concat([])
    }
  },
  computed: {
    initialized() {
      return this.item && this.item.id
    },
    affiliateId() {
      return parseInt(this.$store.state.user.affiliate_id) || 0
    },
    campaignBaseEndpoint() {
      return this.$store.getters.affiliateBaseEndpoint + '/campaign/' + (this.item && this.item.id)
    },
    affiliateManager() {
      return this.$store.state.user.manager != null && typeof(this.$store.state.user.manager.first_name) == 'string'
           ? this.$store.state.user.manager.first_name
           : 'your Affiliate Manager';
    },
    step1Stats() {
      return this.allStats.step1 || []
    },
    step2Stats() {
      return this.allStats.step2 || []
    },
    hasStats() {
      return ((this.step1Stats && this.step1Stats[0]) || (this.step2Stats && this.step2Stats[0]))
    },
    activeCampaignProps() {
      let stats = {}

      Object.keys(this.step1Stats[0] || {})
            .concat(Object.keys(this.step2Stats[0] || {}))
            .forEach((key) => {if(campaignStatProps[key]) {stats[key] = campaignStatProps[key]}})

      return stats
    },
    attributeLegend() {
      let legends = {}
      Object.keys(this.activeCampaignProps).forEach((key) => legends[key] = this.activeCampaignProps[key].legend)
      return legends
    },
    message() {
      if (!this.item)
        return 'Please select a Campaign group from the list on the left.';

      if (this.item && !this.item.id)
        return false

      if (!this.dates || !this.dates[0] || !this.dates[1])
        return 'Please choose a date range using the selector above.';

      if (!this.loading && this.step1Stats.length == 0 && this.step2Stats.length == 0)
        return 'No stats available for the provided date range.';

      return false;
    },
    overage() {
      return [this.step1Stats, this.step2Stats].some(stats => {
        return stats.some(row => {
          return parseInt(row.overage_percent) > 0;
        });
      });
    },
    validCredentials() {
      let stats = this.step1Stats.concat(this.step2Stats)
      // Valid if all rows are success
      return stats.every(row => {
        return row.last_login_status == 'success';
      })
    },
    lastUpdated() {
      var date1 = 0;
      var date2 = 0;

      this.step1Stats.forEach(row => {
        if (row.updated && Date.parse(row.updated) <= Date.parse(row.date).add(1).day())
          date1 = Math.max(Date.parse(row.updated).getTime(), date1);
      });
      this.step2Stats.forEach(row => {
        if (row.updated && Date.parse(row.updated) <= Date.parse(row.date).add(1).day())
          date2 = Math.max(Date.parse(row.updated).getTime(), date2);
      });

      // Take the least of the step1/step2 last updated time
      var date = (date1 && date2)? Math.min(date1, date2): (date1 || date2);

      return date > 0? 'Last updated ' + $.timeago(new Date(date)): date;
    },
    // formatted data for graphs
    graphData() {
      let labels = (this.step1Stats.length > 0? this.step1Stats: this.step2Stats).map(record => {
        let date = new Date(record.date)
        return (date.getUTCMonth() + 1) + '/' + date.getUTCDate()
      })

      let statStep1 = {
        yAxisID: 'main',
        label: 'Step 1',
        data: this.step1Stats.map(record => { return record[this.currentDataView] ? (record[this.currentDataView] + '').replace('%', '') : 0 }),
        borderColor: 'transparent',
        fill: true,
        backgroundColor: 'rgba(0,214,196, .4)',
        postUnit: this.currentDataView.indexOf('percent') > 1 ? '%' : ''
      }
      let statStep2 = {
        yAxisID: 'main',
        label: 'Step 2',
        data: this.step2Stats.map(record => { return record[this.currentDataView] ? (record[this.currentDataView] + '').replace('%', '') : 0 }),
        borderColor: 'transparent',
        fill: true,
        backgroundColor: 'rgba(87,168,244, .4)',
        postUnit: this.currentDataView.indexOf('percent') > 1 ? '%' : ''
      }
      let statEPC = {
        label: 'EPC',
        data: this.step1Stats.map(record => { return record['epc'].replace('$', '') }),
        borderColor: 'rgba(75,49,61,0.6)',
        fill: false,
        backgroundColor: 'transparent',
        borderWidth: 3,
        radius: 1,
        hasBand: true,
        preUnit: '$',
      }
      // show/hide data without doing a detroy->render circle everytime data is updated
      if (this.step1Stats.length == 0 || this.currentDataView == 'take_rate_percent') {
        statStep1.hidden = true
        statStep1.noLegend = true
      }
      if (this.step2Stats.length == 0 || this.currentDataView == 'conversion_percent') {
        statStep2.hidden = true
        statStep2.noLegend = true
      }

      return { labels, datasets: [ statEPC, statStep2, statStep1 ] }
    }
  },
  watch: {
    item(newVal, oldVal) {
      if (!this.item || this.previousItem != this.item) {
        this.allStats     = {}
        this.previousItem = null
      }

      this.requestStats()
    },
    dates() {
      if (!this.dates || !this.dates[0] || !this.dates[1])
        this.allStats = {};
      this.requestStats()
    },
    activeTabs(newVal){
      this.$store.commit('setPreference', { name: 'performanceTabs', value: newVal.concat([]) })
    }
  },
  methods: {
    requestStats(force = false) {
      // From and to dates are required
      if (!this.item || !this.item.id || !this.dates || !this.dates[0] || !this.dates[1])
        return this.loading = false;

      this.dates[0] = this.dates[0] instanceof Date? this.dates[0].toISOString(): this.dates[0];
      this.dates[1] = this.dates[1] instanceof Date? this.dates[1].toISOString(): this.dates[1];

      // Check if dates really changed
      if (!force && (this.dates[0] == this.previousFromDate && this.dates[1] == this.previousToDate && this.previousItem == this.item))
        return this.loading = false;

      this.refreshing       = force;
      this.loading          = true;
      this.allStats         = {};

      // Keep track of dates and item
      this.previousFromDate = this.dates[0];
      this.previousToDate   = this.dates[1];
      this.previousItem     = this.item;

      var apiEndpoint = this.campaignBaseEndpoint + '/stats-request' + '?from=' + this.dates[0] + '&to=' + this.dates[1];
      this.$http.get(apiEndpoint, {requestId: 'stats-request', context: this._uid})
          .then((response) => {
            this.allStats = response.data.data[0]
            this.loading = this.requestedOverage = this.refreshing = false
          }).catch((error) => {
            if (this.$http.isApiError(error)) {
              this.allStats = {}
              this.loading = this.requestedOverage = this.refreshing = false
            }
          })

      return true;
    },
    showStatLegend(ev, stepIndex, prop) {
      this.mouseEvent = ev
      if (prop == 'date') return

      setTimeout(() => {
        if (this.mouseEvent === ev && this.$refs['stat-header-' + prop] && this.$refs['stat-popover-' + prop]) {
          this.$refs['stat-popover-' + prop][0].referenceElm = (this.$refs['stat-header-' + prop][stepIndex] || this.$refs['stat-header-' + prop][0])
          this.$refs['stat-popover-' + prop][0].showPopper   = true
        }
      }, 150)
    },
    hideStatLegend(ev, stepIndex, prop) {
      this.mouseEvent = null
      if (prop == 'date') return

      if (this.$refs['stat-header-' + prop])
        this.$refs['stat-popover-' + prop][0].showPopper = false
    },
    stickyCell(ev) {
      if (ev.target.className.indexOf('campaign-table-wrapper') !== -1) {
        $('.campaign-table-wrapper').not(ev.target).scrollTop(ev.target.scrollTop)
        $('.campaign-table-wrapper th').css('transform', 'translate( '+ ev.target.scrollLeft + 'px,' + ev.target.scrollTop + 'px)')
      } else {
        $('.campaign-table-scroller').not(ev.target).scrollLeft(ev.target.scrollLeft)
      }
    },
    colorCell(name, value) {
      let className = '';
      this.attributeLegend[name] && this.attributeLegend[name].some((o) => {
        if (parseFloat(value) <= o.value)
          return className = o.style;
      });

      return className;
    }
  },
  mounted() {
    this.requestStats()
  },
};
</script>
<style lang="scss">
.campaign-summary {
  &.el-popover {
    width: 450px;
    padding: 16px;
  }
  .el-popover__title {
    margin-bottom: 16px;
  }
}
@media(max-width: 450px) {
  .campaign-summary.el-popover {
    width: 100%;
  }
}
</style>
<style lang="scss" scoped>
h1 {
  margin-bottom: 12px;
  color: #111;
}
h2 {
  color: #555;
}
.refresh,
.share {
  color: #fff;
  font-size: 26px;
  position: relative;
  left: 6px;
  top: 4px;
  cursor: pointer;

  &:hover {
    color: #dee8ed;
  }

  &.spin {
    animation: spin infinite linear 1s;
  }
}
.share {
  top: 6px;
  left: 0;
  float: right;
}
@keyframes spin {
  from {transform:rotate(360deg);}
  to {transform:rotate(0deg);}
}
.chart-title {
    display: block;
    width: 200px;
    margin: 10px auto;
}
.data-content /deep/ .el-loading-text {
  text-align: center;
}
.data-content .el-collapse-item {
  margin-bottom: 15px;
}
.affiliate-id {
  float: right;
  position: relative;
  top: -60px;
  margin-bottom: -40px;
}
.graph-selector {
  margin-top: 10px;
}
.message {
  min-height: 300px;
  padding: 150px 0;
  text-align: center;
  background: #F1F1F1;
  border-radius: 5px;
  font-size: 16px;
  color: #777;
  clear: both;
  &.loading {
    background: #F9F9F9;
  }
  /deep/ .el-loading-spinner {
    margin-top: 0;
    top: calc(50% + 20px)
  }
  p {
    text-align: center;
  }
}
.red {
  background-color: #DC392A !important;
  border-radius: 3px !important;
  color: #EEE;
  border-radius: 3px !important;
}
.green {
  background-color: #00AD1B !important;
  color: #FFF;
}
.yellow {
  background-color: #FEE735 !important;
  border-radius: 3px !important;
}
.orange {
  background-color: #F78724 !important;
  border-radius: 3px !important;
}

.stat-popover__title {
  font-weight: bold;
  font-size: 1.3em;
  margin-bottom: 5px;
}
.stat-legend {
  &__title {
    margin-top: 10px;
  }
  &__li {
    margin-bottom: 5px;
  }
  &__square {
    display:inline-block;
    vertical-align: middle;
    margin-right: 10px;
    width: 40px;
    height: 20px;
    border: 1px solid black;
    border-radius: 0 !important;
  }
}

.campaign-table-wrapper {
  position: relative;
  margin-bottom: 20px;
  border-right: 1px solid #ccc;
  overflow: hidden;
  overflow-y: scroll;
  max-height: calc(45vh);
  -webkit-backface-visibility: hidden;
  -webkit-transform-style: preserve-3d;
  .campaign-table-scroller {
    width: 100%;
    overflow-y: visible;
    overflow-x: scroll;
    -webkit-overflow-scrolling: auto;
  }
  .campaign-table {
    width: calc(100% -  100px);
    min-width: 750px;
    box-sizing: border-box;
    margin-left: 100px;
    -webkit-backface-visibility: hidden;

    td, th {
      position: relative;
      white-space: nowrap;
      overflow: hidden;
      text-align: right;
      transition: all .1s ease;
      transition-delay: .05s;
      transform-origin: center;
      span{
        display: table-caption;
        padding: 10px 4px;
        position: relative;
        left: 5px;
      }
    }

    td:first-child {
      z-index: 1;
      text-align: left;
      white-space: nowrap;
      will-change: transform;
      min-width: 100px;
      width: 100px;
      display: block;
      position: absolute;
      left: 0;
    }

    td {
      background: #F5F5F5;
      color: #444;
      border-bottom: 1px solid rgba(230, 230, 230, 0.7);
    }
    td:nth-child(2n){
      background: #efefef;
    }
    th:first-child {
      z-index: 10;
      min-width: 100px;
      width: 100px;
      position: absolute;
      left: 0px;
      will-change: transform;
      border: none;
    }
    th {
      font-weight: bold;
      height: 50px;
      padding: 5px;
      padding-top: 18px;
      padding-bottom: 17px;
      vertical-align: middle;
      border-bottom: 2px solid rgba(230, 230, 230, 0.7);
      font-size: 15px;
      color: #222;
      background-color: #fff;
      white-space: nowrap;
      text-align: center;
    }
    tbody tr:last-child {
      margin-bottom: 35px;
    }
  }
}

/*
 * RESPONSIVE
 */
@media(max-width: 767px) {
  .affiliate-id {
    float: none;
    margin-bottom: 20px;
    position: static;

    h1 {
      font-size: 16px;
    }

  }
  .campaign-table {
    th {
      font-weight: normal;
      padding: 2px;
      padding-top: 8px;
      padding-bottom: 7px;
      font-size: 13px;
      text-overflow: clip;
    }
    button {
      width: 100%;
    }
  }
  .graph-selector {
    width: 100%;
  }
  .performance-charts .el-collapse {
    padding: 0 0 20px 0;
  }
  .campaign-table-wrapper {
  .campaign-table {
      max-width: 750px;

    th {
      font-weight: normal;
      padding: 2px;
      padding-top: 8px;
      padding-bottom: 7px;
      font-size: 13px;
      text-overflow: clip;
    }
    td {
        font-size: 12px;
        padding: 4px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// campaign.vue?756359ae